/**
 * ============================================================
 * Kişi yolculuğu — 8 aşamanın TEK kaynağı
 * ============================================================
 * Model A (tek yolculuk): aday → öğrenci → yenileme aynı dosyada.
 * Aşama bir kolonda saklanmaz; crm_journey view'i (Faz 1.3) gerçek
 * veriden hesaplar. Bu dosya yalnız arayüz tarafını tanımlar:
 * ad, kısa açıklama, "Yapman gereken" yönergesi, elle/oto ve renk.
 *
 * Renk kuralı: turuncu (primary) aşama rengi DEĞİLDİR — yalnız birincil
 * aksiyon ve "şu an buradasınız" işareti için ayrılmıştır.
 */
import type { ThemeMode } from "../../theme";

export type StageKey =
  | "yeni"
  | "ulasildi"
  | "deneme"
  | "teklif"
  | "kayit"
  | "aktif"
  | "yenileme"
  | "kapandi";

/** elle: admin ilerletir · oto: veriden hesaplanır · karma: elle başlar, veriyle kapanır */
export type StageMode = "elle" | "oto" | "karma";

export interface StageTone {
  fg: string;
  bg: string;
}

export interface StageDef {
  key: StageKey;
  label: string;
  /** Aşama çizgisinde başlığın altındaki kısa açıklama */
  short: string;
  /** "Şu an buradasınız" bandındaki yönerge */
  todo: string;
  mode: StageMode;
  /** Otomatik ilerleyen aşamalarda, ne olunca geçileceği */
  autoHint?: string;
  tone: Record<ThemeMode, StageTone>;
}

export const STAGES: readonly StageDef[] = [
  {
    key: "yeni",
    label: "Yeni",
    short: "İlk arama",
    todo: "İlk aramayı yap",
    mode: "elle",
    tone: {
      light: { fg: "#1d4ed8", bg: "#dbeafe" },
      dark: { fg: "#93c5fd", bg: "#1e3a5f" },
    },
  },
  {
    key: "ulasildi",
    label: "Ulaşıldı",
    short: "İhtiyaç ve hedef",
    todo: "Hedefini ve uygun saatlerini not al",
    mode: "elle",
    tone: {
      light: { fg: "#6d28d9", bg: "#ede9fe" },
      dark: { fg: "#c4b5fd", bg: "#3b2a6b" },
    },
  },
  {
    key: "deneme",
    label: "Seviye & Deneme",
    short: "Seviye testi ve deneme dersi",
    todo: "Deneme dersini planla",
    mode: "elle",
    tone: {
      light: { fg: "#b45309", bg: "#fef3c7" },
      dark: { fg: "#fcd34d", bg: "#4a3410" },
    },
  },
  {
    key: "teklif",
    label: "Teklif & Ödeme",
    short: "Paket ve ödeme",
    todo: "Paketi öner, ödeme linkini gönder",
    mode: "karma",
    autoHint: "Ödeme gelince kendiliğinden Kayıt'a geçer",
    tone: {
      light: { fg: "#0e7490", bg: "#cffafe" },
      dark: { fg: "#67e8f9", bg: "#123f4a" },
    },
  },
  {
    key: "kayit",
    label: "Kayıt",
    short: "Sınıfa veya eğitmene yerleştirme",
    todo: "Öğrenciyi sınıfa ya da eğitmene yerleştir",
    mode: "oto",
    autoHint: "Sınıf veya ders tanımlanınca Aktif'e geçer",
    tone: {
      light: { fg: "#0f766e", bg: "#ccfbf1" },
      dark: { fg: "#5eead4", bg: "#123f3a" },
    },
  },
  {
    key: "aktif",
    label: "Aktif Öğrenim",
    short: "Ders, ödev, memnuniyet",
    todo: "Devam, ödev ve memnuniyeti takip et",
    mode: "oto",
    autoHint: "Ders hakkı ya da program bitince Yenileme'ye geçer",
    tone: {
      light: { fg: "#15803d", bg: "#dcfce7" },
      dark: { fg: "#86efac", bg: "#14432a" },
    },
  },
  {
    key: "yenileme",
    label: "Yenileme",
    short: "Sonraki paket",
    todo: "Yenileme görüşmesini yap",
    mode: "oto",
    autoHint: "Yeni ödeme gelince döngü Kayıt'tan devam eder",
    tone: {
      light: { fg: "#be185d", bg: "#fce7f3" },
      dark: { fg: "#f9a8d4", bg: "#4a1832" },
    },
  },
  {
    key: "kapandi",
    label: "Kapandı",
    short: "Mezun ya da kayıp",
    todo: "Sonucu işaretle: mezun ya da kayıp (sebebiyle)",
    mode: "elle",
    tone: {
      light: { fg: "#475569", bg: "#e2e8f0" },
      dark: { fg: "#cbd5e1", bg: "#334155" },
    },
  },
] as const;

export const STAGE_BY_KEY: Record<StageKey, StageDef> = Object.fromEntries(
  STAGES.map((s) => [s.key, s])
) as Record<StageKey, StageDef>;

export const stageIndex = (key: StageKey): number =>
  STAGES.findIndex((s) => s.key === key);

export const MODE_LABEL: Record<StageMode, string> = {
  elle: "Elle",
  oto: "Otomatik",
  karma: "Elle başlar, ödemeyle kapanır",
};
